import { Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  FetchAllEventsAction,
  FetchFlaggedEventsAction,
  SetFlagDecisionAction,
} from "../../Store/Actions/EventMasterActions";
import { EventMasterActionConst } from "../../Store/Constants/EventMasterConstants";
import DashboardCharts from "./chart";

const Spinner = () => (
  <div className="flex justify-center items-center py-24">
    <Loader2 className="animate-spin text-blue-600" size={32} />
  </div>
);

const getStatusStyle = (status) => {
  if (status === "Active") return "bg-[#3FE36B]";
  if (status === "Archived") return "bg-gray-400";
  if (status === "Draft") return "bg-gray-300";
  return "bg-[#DFCD00]";
};

const MainEventList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const processedRef = useRef(null);

  const { events, flaggedEvents, loader, actionSuccess, actionError } = useSelector(
    (state) => state.eventMaster
  );

  useEffect(() => {
    dispatch(FetchAllEventsAction());
    dispatch(FetchFlaggedEventsAction());
  }, [dispatch]);

  useEffect(() => {
    if (actionSuccess && !processedRef.current) {
      processedRef.current = actionSuccess;
      toast.success(actionSuccess.message || "Action completed successfully.");
      dispatch({ type: EventMasterActionConst.CLEAR_EVENT_ACTION_STATE });
      processedRef.current = null;
    }
  }, [actionSuccess, dispatch]);

  useEffect(() => {
    if (actionError) {
      toast.error(actionError.message || "Action failed. Please try again.");
      dispatch({ type: EventMasterActionConst.CLEAR_EVENT_ACTION_STATE });
    }
  }, [actionError, dispatch]);

  const handleFlagDecision = (id, decision) => {
    dispatch(SetFlagDecisionAction(id, decision));
  };

  const pendingFlags = flaggedEvents.filter((f) => f.decision === null);

  return (
    <div className="w-[95%] mx-auto my-8">
      <div>
        <h1 className="font-popinsBold text-2xl">Events & Collaborations</h1>
        <p className="font-popinsRegular text-textColor mt-1 text-sm">
          Manage all events and collaborations across the platform
        </p>
      </div>

      {/* Metrics Summary — hardcoded per design */}
      <div className="lg:w-[85%]">
        <h3 className="font-popinsSemiBold text-xl mt-8">Metrics Summary</h3>
        <div className="flex flex-col gap-y-4 lg:gap-y-0 lg:flex-row lg:justify-between mt-5">
          <div className="w-full lg:w-[24%] border border-border rounded-lg shadow px-6 py-10">
            <p className="font-popinsMedium text-base">Active Events</p>
            <p className="font-popinsSemiBold text-2xl pt-1">125</p>
          </div>
          <div className="w-full lg:w-[24%] border border-border rounded-lg shadow px-6 py-10">
            <p className="font-popinsMedium text-base">Volunteer Sign-Ups</p>
            <p className="font-popinsSemiBold text-2xl pt-1">350</p>
          </div>
          <div className="w-full lg:w-[24%] border border-border rounded-lg shadow px-6 py-10">
            <p className="font-popinsMedium text-base">Collaboration Workspaces</p>
            <p className="font-popinsSemiBold text-2xl pt-1">80</p>
          </div>
          <div className="w-full lg:w-[24%] border border-border rounded-lg shadow px-6 py-10">
            <p className="font-popinsMedium text-base">Flagged Events</p>
            <p className="font-popinsSemiBold text-2xl pt-1">{pendingFlags.length}</p>
          </div>
        </div>
      </div>

      {/* Event Master List Table */}
      <div className="lg:w-[85%]">
        <h3 className="font-popinsSemiBold text-xl mt-8">Event Master List</h3>
        {loader ? (
          <Spinner />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-300 panelScroll mt-5">
            {events.length === 0 ? (
              <div className="p-10 text-center text-sm font-popinsRegular text-[#61758A]">
                No events found.
              </div>
            ) : (
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Event Details
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Posted By
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((item) => (
                    <tr key={item.id} className="border-t">
                      <td className="px-4 py-4 text-sm font-popinsRegular">
                        {item.title}
                      </td>
                      <td className="px-4 py-4 text-sm font-popinsRegular text-[#61758A]">
                        {item.postedBy}
                      </td>
                      <td className="px-4 py-4 font-popinsRegular">
                        <div
                          className={`${getStatusStyle(item.status)} flex justify-center rounded-lg px-4 py-1 font-popinsMedium text-xs w-24`}
                        >
                          {item.status}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm font-popinsRegular text-[#61758A]">
                        {item.schedule}
                      </td>
                      <td className="px-4 py-4 text-sm font-popinsRegular text-[#61758A]">
                        {item.location}
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => navigate(`/dashboard/event-master-detail/${item.id}`)}
                          className="text-blue-600 font-popinsMedium text-xs hover:underline"
                        >
                          View Collaboration Workspace
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Flagged Events Review */}
      <div className="lg:w-[85%]">
        <h3 className="font-popinsSemiBold text-xl mt-8">Flagged Events Review</h3>
        {pendingFlags.length === 0 ? (
          <p className="font-popinsRegular text-[#61758A] text-sm mt-4">
            No pending flagged events.
          </p>
        ) : (
          <div className="flex flex-col gap-y-6 mt-5">
            {pendingFlags.map((flag) => (
              <div key={flag.id} className="flex flex-col-reverse lg:flex-row gap-x-6">
                <div className="w-full lg:w-[65%] mt-3 lg:mt-0">
                  <p className="font-popinsSemiBold text-base">{flag.eventTitle}</p>
                  <p className="font-popinsRegular text-[#61758A] text-xs pt-1">
                    {flag.reason} — flagged by {flag.flaggedByEmail}
                  </p>
                  <div className="flex gap-x-3 mt-5">
                    <button
                      onClick={() => handleFlagDecision(flag.id, "approved")}
                      className="relative overflow-hidden py-2 rounded-lg bg-[#3FE36B] text-black font-popinsSemiBold text-sm
                           transform transition duration-300 ease-in-out
                           hover:bg-[#3FE36B]/90 hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)]
                           active:scale-95 active:shadow-inner focus:outline-none flex items-center px-6 gap-x-2"
                    >
                      <span>Approve</span>
                    </button>
                    <button
                      onClick={() => handleFlagDecision(flag.id, "removed")}
                      className="relative overflow-hidden py-2 rounded-lg bg-[#DD0000] text-white font-popinsSemiBold text-sm
                           transform transition duration-300 ease-in-out
                           hover:bg-[#DD0000]/90 hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)]
                           active:scale-95 active:shadow-inner focus:outline-none flex items-center px-6 gap-x-2"
                    >
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12">
          <h4 className="font-popinsSemiBold text-xl">Analytics</h4>
          <DashboardCharts />
        </div>
      </div>
    </div>
  );
};

export default MainEventList;
